import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, LoadingBlock, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { VERIFICATION_LABELS } from "@/lib/format";
import { UserRound, ShieldCheck, Mail, Phone, FileCheck } from "lucide-react";

const VALS = ["da_verificare", "verificato", "rifiutato", "scaduto"];

export default function GestioneProfessionisti() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-pros"], queryFn: async () => (await api.get("/admin/professionals")).data });
  const pros = data?.professionals || [];
  const [edit, setEdit] = useState(null);
  const [vals, setVals] = useState({});

  const openEdit = (p) => { setEdit(p); setVals({ verification_status: p.verification_status, board_status: p.board_status, insurance_status: p.insurance_status, vat_status: p.vat_status }); };

  const save = async () => {
    try { await api.patch(`/admin/professionals/${edit.user_id}/verify`, vals); toast.success("Verifica aggiornata."); setEdit(null); qc.invalidateQueries({ queryKey: ["admin-pros"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const quickVerify = async (p) => {
    try { await api.patch(`/admin/professionals/${p.user_id}/verify`, { verification_status: "verificato", board_status: "verificato", insurance_status: "verificato", vat_status: "verificato" }); toast.success("Professionista verificato!"); qc.invalidateQueries({ queryKey: ["admin-pros"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <DashboardLayout title="Professionisti & verifica documentale">
      {isLoading ? <LoadingBlock /> : pros.length === 0 ? <EmptyState title="Nessun professionista" /> : (
        <div className="space-y-4">
          {pros.map((p) => (
            <Card key={p.user_id} data-testid={`admin-pro-${p.user_id}`}>
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <div className="flex gap-4">
                  <span className="h-14 w-14 rounded-2xl bg-[#E0EFEA] text-[#087A73] flex items-center justify-center shrink-0"><UserRound className="h-7 w-7" /></span>
                  <div>
                    <p className="font-bold text-[#102A2A]">{p.first_name} {p.last_name}</p>
                    <p className="text-sm text-[#087A73] font-semibold">{p.profession || "—"}</p>
                    <p className="text-sm text-[#5C6B68] flex items-center gap-1 mt-1"><Mail className="h-4 w-4" /> {p.email}</p>
                    <p className="text-sm text-[#5C6B68] flex items-center gap-1"><FileCheck className="h-4 w-4" /> Albo: {p.registration_number || "—"} · P.IVA: {p.vat_number || "—"}</p>
                  </div>
                </div>
                <div className="flex flex-col items-start lg:items-end gap-2">
                  <StatusBadge status={p.verification_status} label={VERIFICATION_LABELS[p.verification_status]} testid={`pro-verif-${p.user_id}`} />
                  <div className="flex gap-2">
                    {p.verification_status !== "verificato" && <Button data-testid={`quick-verify-${p.user_id}`} onClick={() => quickVerify(p)} className="bg-[#087A73] text-white rounded-xl h-10 px-4 text-sm"><ShieldCheck className="mr-1 h-4 w-4" /> Approva</Button>}
                    <Button data-testid={`edit-verify-${p.user_id}`} variant="outline" onClick={() => openEdit(p)} className="rounded-xl h-10 px-4 text-sm border-2 border-[#D2E3DB]">Modifica stati</Button>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#EAF3EF] grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[["board_status", "Albo"], ["insurance_status", "Polizza RC"], ["vat_status", "Partita IVA"]].map(([k, l]) => (
                  <div key={k} className="text-center"><p className="text-xs text-[#5C6B68] mb-1">{l}</p><StatusBadge status={p[k]} label={VERIFICATION_LABELS[p[k]] || "—"} /></div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-white rounded-2xl max-w-md">
          <DialogHeader><DialogTitle>Verifica documentale (simulata)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {[["verification_status", "Stato profilo"], ["board_status", "Iscrizione albo"], ["insurance_status", "Polizza RC"], ["vat_status", "Partita IVA"]].map(([k, l]) => (
              <div key={k}>
                <Label>{l}</Label>
                <Select value={vals[k]} onValueChange={(v) => setVals({ ...vals, [k]: v })}>
                  <SelectTrigger data-testid={`verify-${k}`} className="h-11 rounded-xl mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{VALS.map((v) => <SelectItem key={v} value={v}>{VERIFICATION_LABELS[v]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
            <Button data-testid="verify-save" onClick={save} className="w-full bg-[#087A73] text-white rounded-xl h-11">Salva verifiche</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
